import type { CSSProperties, ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { ImageBleed } from 'types';
import type { ModuleRowColumn, ModuleRowModule } from 'types/capi/article';

import BrandedHeading from '~/components/Content/BrandedHeading';
import BrandedQuote from '~/components/Content/BrandedQuote';
import ModuleImpression from '~/components/ModuleImpression';
import useIsMobileViewport from '~/hooks/useIsMobileViewport';
import ContentBlock from '~/modules/ste/ContentBlock';
import EmbedBlock from '~/modules/ste/EmbedBlock';
import EmbeddedBrandedHeading from '~/modules/ste/embedded/EmbeddedBrandedHeading';
import EmbeddedBrandedQuote from '~/modules/ste/embedded/EmbeddedBrandedQuote';
import EmbeddedContentBlock from '~/modules/ste/embedded/EmbeddedContentBlock';
import EmbeddedEmbedBlock from '~/modules/ste/embedded/EmbeddedEmbedBlock';
import EmbeddedImageGrid from '~/modules/ste/embedded/EmbeddedImageGrid';
import ImageGrid from '~/modules/ste/ImageGrid';
import ProductList from '~/modules/ste/ProductList';
import {
  getModuleImpressionProps,
  rendersModule
} from '~/utils/moduleImpression';
import type { ModulePositions } from '~/utils/modulePositions';
import { isColumnHiddenOnMobile } from '~/utils/moduleVisibility';
import { isSideBleedColumn, type SideBleed } from '~/utils/sideBleed';

type ModuleRowContextValue = {
  narrow: boolean;
};

const ModuleRowContext = createContext<ModuleRowContextValue | null>(null);

export function useModuleRowContext() {
  return useContext(ModuleRowContext);
}

type Props = {
  module: ModuleRowModule;
  modulePositions: ModulePositions;
};

type ColumnModule = ModuleRowColumn['modules'][number];

export type ColumnPosition = 'first' | 'last' | 'middle';

const containerStyle = { containerType: 'inline-size' } as const;

const gridByColumnCount: Record<number, string> = {
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4'
};

export default function ModuleRow({ module, modulePositions }: Props) {
  const { columns } = module;
  const mobile = useIsMobileViewport();
  const narrow = (columns?.length ?? 0) >= 3;
  const contextValue = useMemo(() => ({ narrow }), [narrow]);

  if (!columns?.length) return null;

  if (columns.length === 1) {
    const column = columns[0]!;
    const columnHidden = isColumnHiddenOnMobile(column);

    return column.modules.map((childModule, modIndex) => (
      <BaseModule
        key={modIndex}
        module={childModule}
        modulePosition={modulePositions.get(childModule)}
        hiddenOnMobile={columnHidden || !!childModule.mobile_hidden}
        mobile={mobile}
      />
    ));
  }

  const gridCols =
    gridByColumnCount[columns.length] ?? 'grid-cols-1 md:grid-cols-2';
  const allColumnsHiddenOnMobile = columns.every(isColumnHiddenOnMobile);
  const wrapperClasses = `flex w-full items-start justify-center max-md:pb-4 ${
    allColumnsHiddenOnMobile ? 'max-md:hidden' : ''
  }`;

  return (
    <ModuleRowContext.Provider value={contextValue}>
      <div className={wrapperClasses} style={containerStyle}>
        <div
          className={`content-width grid w-full gap-x-4 gap-y-6 md:gap-6 ${gridCols}`}
        >
          {columns.map((column, colIndex) => (
            <Column
              key={colIndex}
              column={column}
              index={colIndex}
              mobile={mobile}
              mobileOrder={column.mobile_order ?? colIndex}
              modulePositions={modulePositions}
              numColumns={columns.length}
            />
          ))}
        </div>
      </div>
    </ModuleRowContext.Provider>
  );
}

// A module the latched viewport hides is not rendered at all, not merely
// hidden: the view's manifest already says it has no box, and a rotation
// that later revealed a hidden-but-mounted module would let it emit events
// with no ordinal against a manifest that never listed it.
function BaseModule({
  hiddenOnMobile,
  mobile,
  module,
  modulePosition
}: {
  hiddenOnMobile?: boolean;
  mobile: boolean;
  module: ColumnModule;
  modulePosition?: number;
}) {
  if (hiddenOnMobile && mobile) return null;

  const inner = renderBaseModule(module, modulePosition);
  if (!inner) return null;

  const tracked = (
    <ModuleImpression
      columnCount={1}
      modulePosition={modulePosition}
      moduleType={module.type}
      {...getModuleImpressionProps(module)}
    >
      {inner}
    </ModuleImpression>
  );
  if (!hiddenOnMobile) return tracked;

  return <div className="max-md:hidden">{tracked}</div>;
}

function renderBaseModule(module: ColumnModule, modulePosition?: number) {
  if (!rendersModule(module)) return null;

  switch (module.type) {
    case 'branded-heading':
      return <BrandedHeading module={module} />;
    case 'branded-quote':
      return <BrandedQuote quote={module} />;
    case 'content-block':
      return <ContentBlock {...module} />;
    case 'embed':
      return <EmbedBlock embed={module} modulePosition={modulePosition} />;
    case 'image-grid':
      return <ImageGrid module={module} modulePosition={modulePosition} />;
    case 'product-list':
      return (
        <ProductList
          {...module}
          columnCount={1}
          modulePosition={modulePosition}
          pageType="article"
        />
      );
    default:
      return null;
  }
}

const contentBlockCollapseTypes: ColumnModule['type'][] = [
  'branded-heading',
  'branded-quote'
];

const bleedBaseClasses =
  'desktop:[--bleed:max(0px,calc(50cqw-35.25rem))] desktop:w-[calc(100%+var(--bleed))]';

function getColumnBleed(
  hasBleed: boolean,
  position: ColumnPosition
): ImageBleed {
  if (!hasBleed) return 'none';
  if (position === 'first') return 'left';
  if (position === 'last') return 'right';

  return 'none';
}

export function getBleedClass(bleed: ImageBleed, numColumns: number) {
  if (bleed === 'none') return '';

  if (bleed === 'left')
    return `${bleedBaseClasses} desktop:ml-[calc(var(--bleed)*-1)] ${
      numColumns > 2 ? 'desktop:pr-[94px]' : ''
    }`;

  if (bleed === 'right')
    return `${bleedBaseClasses} ${numColumns > 2 ? 'desktop:pl-[94px]' : ''}`;

  return '';
}

const sideBleedAlignment: Record<SideBleed, string> = {
  left: 'desktop:justify-end',
  right: 'desktop:justify-start'
};

const sideBleedOuterPadding: Record<SideBleed, string> = {
  left: 'lg:pl-8',
  right: 'lg:pr-8'
};

const textModuleTypes: ColumnModule['type'][] = [
  'branded-heading',
  'branded-quote',
  'content-block',
  'embed'
];

function getSideBleedFit(module: ColumnModule) {
  if (textModuleTypes.includes(module.type)) return 'text';
  if (module.type === 'product-list' && module.display_type !== 'block')
    return 'products';

  return undefined;
}

function SideBleedFrame({
  bleed,
  children,
  fit
}: {
  bleed: SideBleed;
  children: ReactNode;
  fit: 'text' | 'products';
}) {
  const isText = fit === 'text';

  return (
    <div
      className={`flex ${getBleedClass(bleed, 3)} ${sideBleedAlignment[bleed]} ${
        isText ? sideBleedOuterPadding[bleed] : ''
      }`}
    >
      <div className={isText ? 'w-full desktop:max-w-[360px]' : 'w-full'}>
        {children}
      </div>
    </div>
  );
}

function alignToSideBleed(
  inner: ReactNode,
  {
    bleed,
    module,
    numColumns
  }: { bleed: ImageBleed; module: ColumnModule; numColumns: number }
) {
  const fit = getSideBleedFit(module);
  if (!isSideBleedColumn(bleed, numColumns) || !fit) return inner;

  return (
    <SideBleedFrame bleed={bleed} fit={fit}>
      {inner}
    </SideBleedFrame>
  );
}

function Column({
  column,
  index,
  mobile,
  mobileOrder,
  modulePositions,
  numColumns
}: {
  column: ModuleRowColumn;
  index: number;
  mobile: boolean;
  mobileOrder: number;
  modulePositions: ModulePositions;
  numColumns: number;
}) {
  const columnPosition: ColumnPosition =
    index === 0 ? 'first' : index === numColumns - 1 ? 'last' : 'middle';
  const bleed = getColumnBleed(!!column.bleed, columnPosition);
  const isHiddenOnMobile = isColumnHiddenOnMobile(column);
  const marginClass =
    bleed === 'left' && columnPosition === 'first'
      ? 'md:-ml-8 desktop:ml-0'
      : bleed === 'right' && columnPosition === 'last'
        ? 'md:-mr-8 desktop:mr-0'
        : '';

  return (
    <div
      className={`flex flex-col gap-6 max-md:order-[var(--mobile-order)] md:sticky md:top-20 md:self-start ${marginClass} ${
        isHiddenOnMobile ? 'max-md:hidden' : ''
      }`}
      style={{ '--mobile-order': mobileOrder } as CSSProperties}
    >
      {column.modules.map((childModule, modIndex) => (
        <EmbeddedModule
          key={modIndex}
          bleed={bleed}
          columnPosition={columnPosition}
          columnHiddenOnMobile={isHiddenOnMobile}
          hiddenOnMobile={!isHiddenOnMobile && !!childModule.mobile_hidden}
          mobile={mobile}
          module={childModule}
          modulePosition={modulePositions.get(childModule)}
          nextType={column.modules[modIndex + 1]?.type}
          numColumns={numColumns}
          prevType={column.modules[modIndex - 1]?.type}
        />
      ))}
    </div>
  );
}

function EmbeddedModule({
  bleed,
  columnHiddenOnMobile,
  hiddenOnMobile,
  mobile,
  module,
  modulePosition,
  nextType,
  numColumns,
  columnPosition,
  prevType
}: {
  bleed: ImageBleed;
  columnHiddenOnMobile: boolean;
  columnPosition: ColumnPosition;
  hiddenOnMobile?: boolean;
  mobile: boolean;
  module: ColumnModule;
  modulePosition?: number;
  nextType?: ColumnModule['type'];
  numColumns: number;
  prevType?: ColumnModule['type'];
}) {
  if (mobile && (columnHiddenOnMobile || hiddenOnMobile)) return null;

  const inner = renderEmbeddedModule({
    bleed,
    columnPosition,
    module,
    modulePosition,
    nextType,
    numColumns,
    prevType
  });
  if (!inner) return null;

  const tracked = (
    <ModuleImpression
      columnCount={numColumns}
      modulePosition={modulePosition}
      moduleType={module.type}
      {...getModuleImpressionProps(module, { embedded: true })}
    >
      {alignToSideBleed(inner, { bleed, module, numColumns })}
    </ModuleImpression>
  );
  if (!hiddenOnMobile) return tracked;

  return <div className="max-md:hidden">{tracked}</div>;
}

function renderEmbeddedModule({
  bleed,
  module,
  modulePosition,
  nextType,
  numColumns,
  columnPosition,
  prevType
}: {
  bleed: ImageBleed;
  columnPosition: ColumnPosition;
  module: ColumnModule;
  modulePosition?: number;
  nextType?: ColumnModule['type'];
  numColumns: number;
  prevType?: ColumnModule['type'];
}) {
  if (!rendersModule(module)) return null;

  switch (module.type) {
    case 'branded-heading':
      return (
        <EmbeddedBrandedHeading
          columnPosition={columnPosition}
          module={module}
        />
      );
    case 'branded-quote':
      return (
        <EmbeddedBrandedQuote columnPosition={columnPosition} quote={module} />
      );
    case 'content-block':
      return (
        <EmbeddedContentBlock
          {...module}
          collapseBottom={
            !!nextType && contentBlockCollapseTypes.includes(nextType)
          }
          collapseTop={
            !!prevType && contentBlockCollapseTypes.includes(prevType)
          }
          columnPosition={columnPosition}
        />
      );
    case 'embed':
      return (
        <EmbeddedEmbedBlock embed={module} modulePosition={modulePosition} />
      );
    case 'image-grid':
      return (
        <EmbeddedImageGrid
          module={module}
          bleed={bleed}
          modulePosition={modulePosition}
          numColumns={numColumns}
        />
      );
    case 'product-list':
      return (
        <ProductList
          {...module}
          bleed={bleed}
          columnCount={numColumns}
          modulePosition={modulePosition}
          pageType="article"
        />
      );
    default:
      return null;
  }
}
